import React, { useState, useCallback } from 'react';
import { Icon } from '../../components/Icon';
import { ImportSteps } from '../../components/ImportSteps';
import { ImportPreviewTable } from '../../components/ImportPreviewTable';
import { BulkAssignmentModal } from '../../components/BulkAssignmentModal';
import { parseExcelFile, downloadTemplate } from '../../utils/excelParser';
import type { ImportRow, ImportStep } from '../../types/import';
import { useImports } from '../../hooks/useImports';

const Imports: React.FC = () => {
    const [currentStep, setCurrentStep] = useState(0);
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [importData, setImportData] = useState<ImportRow[]>([]);
    const [selectedRows, setSelectedRows] = useState<string[]>([]);
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [uploadError, setUploadError] = useState<string>('');

    const {
        createMassiveLead
    } = useImports();

    const steps: ImportStep[] = [
        {
            id: 'upload',
            title: 'Subir Archivo',
            description: 'Selecciona tu archivo Excel',
            completed: currentStep > 0,
            active: currentStep === 0
        },
        {
            id: 'preview',
            title: 'Vista Previa',
            description: 'Revisa y asigna agentes',
            completed: currentStep > 1,
            active: currentStep === 1
        },
        {
            id: 'import',
            title: 'Importar',
            description: 'Procesa los datos',
            completed: currentStep > 2,
            active: currentStep === 2
        }
    ];

    const handleFileUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setUploadError('');
        setIsProcessing(true);

        try {
            const result = await parseExcelFile(file);
            
            if (result.errors.length > 0) {
                setUploadError(result.errors.join('\n'));
            } else {
                setUploadedFile(file);
                setImportData(result.data);
                setCurrentStep(1);
            }
        } catch (error) {
            setUploadError('Error al procesar el archivo. Asegúrate de que sea un archivo Excel válido.');
        } finally {
            setIsProcessing(false);
        }
    }, []);

    const handleRowSelect = useCallback((rowId: string, selected: boolean) => {
        setSelectedRows(prev => 
            selected 
                ? [...prev, rowId]
                : prev.filter(id => id !== rowId)
        );
    }, []);

    const handleAssignToChange = useCallback((rowId: string, assignTo: string, assignToId: string) => {
        setImportData(prev => 
            prev.map(row => 
                row.id === rowId 
                    ? { ...row, asignadoA: assignTo, asignadoAId: assignToId }
                    : row
            )
        );
    }, []);

    const handleBulkAssign = useCallback((assignTo: string, assignToId: string) => {
        setImportData(prev => 
            prev.map(row => 
                selectedRows.includes(row.id)
                    ? { ...row, asignadoA: assignTo, asignadoAId: assignToId }
                    : row
            )
        );
        setSelectedRows([]);
    }, [selectedRows]);

    const handleImport = useCallback(async () => {
        setIsProcessing(true);
        
        try {
            // Filtrar solo registros válidos y con asignación
            const validData = importData.filter(row => 
                !row.errors || row.errors.length === 0
            );

            if (validData.length === 0) {
                setUploadError('No hay datos válidos para importar');
                return;
            }

            

            // Simular llamada a la API
            // await new Promise(resolve => setTimeout(resolve, 2000));
            
            // Aquí iría la llamada real a la API
            await createMassiveLead(validData);
            
            setCurrentStep(2);
            
            // Mostrar mensaje de éxito
            setTimeout(() => {
                // Resetear todo para una nueva importación
                setCurrentStep(0);
                setUploadedFile(null);
                setImportData([]);
                setSelectedRows([]);
                setUploadError('');
            }, 3000);
            
        } catch (error) {
            setUploadError('Error al importar los datos. Inténtalo de nuevo.');
        } finally {
            setIsProcessing(false);
        }
    }, [importData]);

    const resetImport = () => {
        setCurrentStep(0);
        setUploadedFile(null);
        setImportData([]);
        setSelectedRows([]);
        setUploadError('');
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case 0:
                return (
                    <div className="card p-6">
                        <div className="text-center">
                            <Icon name="upload" className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                                Subir Archivo Excel
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-6">
                                Selecciona un archivo Excel con los datos de los leads
                            </p>
                            
                            <div className="space-y-4">
                                <div>
                                    <input
                                        type="file"
                                        accept=".xlsx,.xls"
                                        onChange={handleFileUpload}
                                        disabled={isProcessing}
                                        className="hidden"
                                        id="file-upload"
                                    />
                                    <label
                                        htmlFor="file-upload"
                                        className={`inline-flex items-center px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer transition-colors ${
                                            isProcessing
                                                ? 'opacity-50 cursor-not-allowed'
                                                : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                                        }`}
                                    >
                                        {isProcessing ? (
                                            <Icon name="loading" className="w-5 h-5 animate-spin mr-2" />
                                        ) : (
                                            <Icon name="upload" className="w-5 h-5 mr-2" />
                                        )}
                                        {isProcessing ? 'Procesando...' : 'Seleccionar Archivo'}
                                    </label>
                                </div>
                                
                                <div>
                                    <button
                                        onClick={downloadTemplate}
                                        className="inline-flex items-center px-4 py-2 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                                    >
                                        <Icon name="download" className="w-4 h-4 mr-2" />
                                        Descargar Plantilla
                                    </button>
                                </div>
                            </div>

                            {uploadedFile && (
                                <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                                    <div className="flex items-center">
                                        <Icon name="check" className="w-5 h-5 text-green-500 mr-2" />
                                        <span className="text-sm text-green-700 dark:text-green-300">
                                            Archivo cargado: {uploadedFile.name}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                );

            case 1:
                return (
                    <div className="space-y-6">
                        <ImportPreviewTable
                            data={importData}
                            onRowSelect={handleRowSelect}
                            selectedRows={selectedRows}
                            onAssignToChange={handleAssignToChange}
                        />
                        
                        <div className="flex justify-between items-center">
                            <button
                                onClick={() => setShowBulkModal(true)}
                                disabled={selectedRows.length === 0}
                                className="flex items-center px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 disabled:text-gray-400 disabled:cursor-not-allowed dark:text-blue-400 dark:hover:text-blue-300"
                            >
                                <Icon name="users" className="w-4 h-4 mr-2" />
                                Asignación Masiva ({selectedRows.length} seleccionados)
                            </button>
                            
                            <div className="flex space-x-3">
                                <button
                                    onClick={resetImport}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 rounded-lg transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleImport}
                                    disabled={importData.filter(row => !row.errors || row.errors.length === 0).length === 0}
                                    className="flex items-center px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 rounded-lg transition-colors"
                                >
                                    <Icon name="upload" className="w-4 h-4 mr-2" />
                                    Importar Datos
                                </button>
                            </div>
                        </div>
                    </div>
                );

            case 2:
                return (
                    <div className="card p-6 text-center">
                        <Icon name="check-circle" className="w-16 h-16 text-green-500 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                            Importación Completada
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400">
                            Se han importado {importData.filter(row => !row.errors || row.errors.length === 0).length} leads exitosamente.
                        </p>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Importar Leads
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">
                        Importa leads desde un archivo Excel
                    </p>
                </div>
            </div>

            {/* Error Display */}
            {uploadError && (
                <div className="card p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                    <div className="flex items-center">
                        <Icon name="alert-circle" className="w-5 h-5 text-red-500 mr-2" />
                        <div>
                            <h4 className="text-sm font-medium text-red-800 dark:text-red-200">
                                Error
                            </h4>
                            <p className="text-sm text-red-700 dark:text-red-300 mt-1 whitespace-pre-line">
                                {uploadError}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Steps */}
            <ImportSteps steps={steps} currentStep={currentStep} />

            {/* Step Content */}
            {renderStepContent()}

            {/* Bulk Assignment Modal */}
            <BulkAssignmentModal
                isOpen={showBulkModal}
                onClose={() => setShowBulkModal(false)}
                onAssign={handleBulkAssign}
                selectedCount={selectedRows.length}
            />
        </div>
    );
};

export default Imports;